import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  HelperControls,
  HelperDescription,
  HelperInstructions,
  HelperName,
  HelperSkills,
  HelperTraining
} from '@/components/helper';
import { Helper, HelpersContext } from '@my-helpers/common';
import { Grid, useTheme } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { Notification } from '@/components/Notification';
import { Tags } from '@/components/Tags';

interface HelperViewProps {
  creating: boolean;
  setCreating: (creating: boolean) => void;
}

const HelperView: React.FC<HelperViewProps> = ({ creating, setCreating }) => {
  const context = useContext(HelpersContext);
  if (context === undefined) {
    throw new Error('useHelpersContext must be used within a HelpersProvider');
  }
  const { view, command } = context;
  const { trainings, currentHelper } = view;
  const navigate = useNavigate();
  const theme = useTheme();

  const [helperForm, setHelperForm] = useState<Helper>({
    name: '',
    description: '',
    status: 'active',
    training_id: '',
    instruction_id: undefined,
    system_roles: [],
    tagged_by: []   
  });

  useEffect(() => {
    if (currentHelper) {
      setHelperForm({
        name: currentHelper.name,
        description: currentHelper.description,
        status: currentHelper.status,
        training_id: currentHelper.training.id || '',
        instruction_id: currentHelper.instructions.find(instruction => instruction.is_default)?.id || undefined,
        system_roles: currentHelper.system_roles,
        tagged_by: currentHelper.tagged_by
      });
    }
  }, [currentHelper]);

  // Memoized validation result - only recalculates when dependencies change
  const isHelperValid = useMemo(() => {
    return !!(
      helperForm.name.trim() &&
      helperForm.name.length <= 50 &&
      helperForm.description.trim() &&
      helperForm.description.length <= 255 &&
      helperForm.training_id !== ''
    );
  }, [helperForm.name, helperForm.description, helperForm.training_id]);


  const handleInputChange = useCallback((field: keyof Helper) => async (value: any) => {
    setHelperForm({
      ...helperForm,
      [field]: value
    });
  }, [helperForm]);

  const handleSubmit = useCallback(async (action: 'create' | 'update' | 'delete') => {
    if (!isHelperValid) {
      return;
    }
    if (view.currentHelper && action === 'delete') {
      await command.deleteHelper(view.currentHelper.id);
      navigate('/helpers');
      return;
    }
    if (view.currentHelper && action === 'update') {
      await command.updateHelper(view.currentHelper.id, helperForm);
      return;
    }
    if (!view.currentHelper && action === 'create') {
      const id = await command.createHelper(helperForm);
      if (id) {
        navigate(`/helpers/${id}`);
      }
    }
  }, [helperForm, isHelperValid, view.currentHelper, command]);

  const handleDefaultInstructionChange = useCallback(async (instructionId?: string) => {
    if (!isHelperValid) {
      return;
    }

  const newInstructionId = helperForm.instruction_id === instructionId ? undefined : instructionId;
    if (view.currentHelper) {
      setHelperForm({
        ...helperForm,
        instruction_id: newInstructionId
      });
      await command.updateHelper(view.currentHelper.id, {
        ...helperForm,
        instruction_id: newInstructionId
      });
    }
  }, [isHelperValid, helperForm, setHelperForm, view.currentHelper, command]);

  const handleChangeTraining = useCallback(async (trainingId: string) => {
    setHelperForm({
      ...helperForm,
      training_id: trainingId
    });
    if (view.currentHelper && isHelperValid) {
      await command.updateHelper(view.currentHelper.id, {
        ...helperForm,
        training_id: trainingId
      });
    }
  }, [isHelperValid, helperForm, setHelperForm, view.currentHelper, command]);

  return (
    <Grid
      container
      spacing={1}
      padding={theme.spacing(3)}
      sx={{ 
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
      {/* Row 1: Name & Training + Controls */}
      <Grid container spacing={2} sx={{ flexShrink: 0 }}>
        <Grid
          size={8}
          display="flex"
          flexDirection="row">
          <HelperName
            name={helperForm.name}
            handleInputChange={handleInputChange('name')}
            handleSubmit={handleSubmit}
          />
          <HelperTraining
            selected={helperForm.training_id}
            trainings={trainings || []}
            handleInputChange={handleChangeTraining}
          />
        </Grid>
        <Grid size={4}>
          <HelperControls
            currentHelper={view.currentHelper}
            isHelperValid={isHelperValid}
            handleSubmit={handleSubmit}
            setCreating={setCreating}
          />
        </Grid>
      </Grid>
      
      {/* Row 2: Description + Tags */}
      <Grid container spacing={2} sx={{ flexShrink: 0 }}>
        <Grid size={8}  display="flex">
          <HelperDescription
            description={helperForm.description}
            handleInputChange={handleInputChange('description')}
            handleSubmit={handleSubmit}
          />
        </Grid>
        <Grid size={4} display="flex">
          <Tags
            tags={[...helperForm.system_roles, ...helperForm.tagged_by]}
            handleInputChange={handleInputChange('tagged_by')}
          />
        </Grid>
      </Grid>
      
      {/* Row 3: Instructions + Skills - FILLS REMAINING HEIGHT */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
        <Grid size={8} display="flex" sx={{ minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
          <HelperInstructions
            currentHelper={view.currentHelper}
            handleChangeDefaultInstruction={handleDefaultInstructionChange}
            createInstruction={command.createInstruction}
            deleteInstruction={command.deleteInstruction}
            updateInstruction={command.updateInstruction}
          />
        </Grid>
        <Grid size={4} display="flex" sx={{ minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
          <HelperSkills
            currentHelper={view.currentHelper}
            addSkills={command.addSkills}
            removeSkills={command.removeSkills}
          />
        </Grid>
      </Grid>
      
      <Notification
        isLoading={view.isLoading}
        error={view.error}
        delay={300}
        autoHideDuration={1200}
        clearError={command.clearError}
      />
    </Grid>
  );
};

export default HelperView;
